import { ErrorPage, LoginPage, RecoveryPage, SettingsPage } from "@payezy/auth";
import { getStaffMeOptions } from "@payezy/api-client/admin/query";
import { Alert, Button, Spinner, Toaster } from "@payezy/ui";
import type { QueryClient } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext, createRoute, createRouter } from "@tanstack/react-router";
import { Brand, Shell } from "./components/Shell";
import { handleAuthProblem, kratos, problemMessage } from "./lib/api";
import { ActionsPage } from "./pages/Actions";
import { AuditPage } from "./pages/Audit";
import { CustomerPage } from "./pages/Customer";
import { CustomersPage } from "./pages/Customers";
import { DashboardPage } from "./pages/Dashboard";
import { OnboardingPage } from "./pages/Onboarding";
import { PartnersPage } from "./pages/Partners";
import { PaymentPage, PaymentsPage } from "./pages/Payments";
import { ReconPage } from "./pages/Recon";
import { SecurityPage } from "./pages/Security";
import { StaffPage } from "./pages/Staff";
import { ViewAsPage } from "./pages/ViewAs";

type Ctx = { queryClient: QueryClient };

const links = { login: "/auth/login", recovery: "/auth/recovery" };

const rootRoute = createRootRouteWithContext<Ctx>()({
  component: () => (
    <>
      <Outlet />
      <Toaster position="top-right" richColors />
    </>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-md p-12">
      <Alert tone="danger" title="Something went wrong">{problemMessage(error)}</Alert>
      <Button className="mt-4" variant="secondary" onClick={() => window.location.reload()}>Reload</Button>
    </div>
  ),
  pendingComponent: () => <div className="flex h-dvh items-center justify-center"><Spinner /></div>,
});

const authRoute = (path: string, Component: () => React.ReactNode) => createRoute({ getParentRoute: () => rootRoute, path, component: Component });

const loginRoute = authRoute("/auth/login", () => <LoginPage kratos={kratos} brand={<Brand />} links={links} mfaSetupUrl="/auth/settings" />);
const recoveryRoute = authRoute("/auth/recovery", () => <RecoveryPage kratos={kratos} brand={<Brand />} links={links} />);
// Outside the AAL2-gated shell: first-time MFA enrollment (and Kratos settings ui_url).
const settingsRoute = authRoute("/auth/settings", () => (
  <SettingsPage
    kratos={kratos}
    brand={<Brand />}
    title="Passkeys & MFA"
    description="The back office requires a passkey or authenticator app. Enroll one here, then confirm it on the next screen."
  />
));
const errorRoute = authRoute("/auth/error", () => <ErrorPage kratos={kratos} brand={<Brand />} homeUrl="/" />);
const deniedRoute = authRoute("/auth/denied", () => {
  const code = new URLSearchParams(window.location.search).get("code");
  return (
    <div className="mx-auto max-w-md p-12">
      <Brand />
      <Alert className="mt-6" tone="danger" title={code === "staff_disabled" ? "Your staff account is disabled" : "Not a staff account"}>
        {code === "staff_disabled" ? "Ask a superadmin to re-enable it." : "Staff accounts are created by a superadmin. Customers sign in at the Payezy app."}
      </Alert>
      <Button className="mt-4" variant="secondary" onClick={() => void kratos.logout(window.location.origin + "/auth/login")}>Sign out</Button>
    </div>
  );
});

/** Staff-only area; /admin/v1/me enforces staff status and AAL2. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(getStaffMeOptions());
    } catch (e) {
      if (handleAuthProblem(e)) return new Promise(() => {});
      throw e;
    }
  },
  component: Shell,
});

const dashboardRoute = createRoute({ getParentRoute: () => appRoute, path: "/", component: DashboardPage });
const customersRoute = createRoute({ getParentRoute: () => appRoute, path: "/customers", component: CustomersPage });
const customerRoute = createRoute({ getParentRoute: () => appRoute, path: "/customers/$orgId", component: CustomerPage });
const paymentRoute = createRoute({ getParentRoute: () => appRoute, path: "/payments/$paymentId", component: PaymentPage });
const onboardingRoute = createRoute({ getParentRoute: () => appRoute, path: "/onboarding", component: OnboardingPage });
const actionsRoute = createRoute({ getParentRoute: () => appRoute, path: "/actions", component: ActionsPage });
const viewAsRoute = createRoute({ getParentRoute: () => appRoute, path: "/view-as", component: ViewAsPage });
const reconRoute = createRoute({ getParentRoute: () => appRoute, path: "/recon", component: ReconPage });
const partnersRoute = createRoute({ getParentRoute: () => appRoute, path: "/partners", component: PartnersPage });
const staffRoute = createRoute({ getParentRoute: () => appRoute, path: "/staff", component: StaffPage });
const securityRoute = createRoute({ getParentRoute: () => appRoute, path: "/security", component: SecurityPage });

const paymentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/payments",
  component: PaymentsPage,
  validateSearch: (s: Record<string, unknown>): { org_id?: string; status?: string; page?: number } => ({
    org_id: typeof s.org_id === "string" ? s.org_id : undefined,
    status: typeof s.status === "string" ? s.status : undefined,
    page: typeof s.page === "number" && s.page > 0 ? s.page : undefined,
  }),
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audit",
  component: AuditPage,
  validateSearch: (s: Record<string, unknown>): { org_id?: string; actor_type?: string; actor_id?: string; action?: string; resource_id?: string } => {
    const v = (k: string) => (typeof s[k] === "string" && s[k] ? (s[k] as string) : undefined);
    return { org_id: v("org_id"), actor_type: v("actor_type"), actor_id: v("actor_id"), action: v("action"), resource_id: v("resource_id") };
  },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  recoveryRoute,
  settingsRoute,
  errorRoute,
  deniedRoute,
  appRoute.addChildren([
    dashboardRoute,
    customersRoute,
    customerRoute,
    paymentsRoute,
    paymentRoute,
    onboardingRoute,
    actionsRoute,
    viewAsRoute,
    reconRoute,
    partnersRoute,
    auditRoute,
    staffRoute,
    securityRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: "intent", defaultPendingMs: 150 });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
